import React, { useState, useEffect } from 'react';
import { getCategoryDetail } from '../services/api';

/**
 * Deep-dive modal for a specific error category.
 * Shows per-code breakdown (Israel/India), subcategories,
 * location repeat analysis, correlations, and voltage analysis.
 */
export default function CategoryDetailModal({ category, days, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!category) return;
    setLoading(true);
    setError(null);
    getCategoryDetail(category.replace(/ /g, '_').toLowerCase(), days)
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [category, days]);

  if (!category) return null;

  const catKey = data?.category || '';
  const isDriveOrRotor = catKey === 'drive_motor' || catKey === 'rotor_cleaning';
  const isComm = catKey === 'communication';
  const isBattery = catKey === 'battery_power';

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20 }}>{category} — Deep Analysis</h2>
            <span style={{ fontSize: 13, color: '#636e72' }}>Last {days} days</span>
          </div>
          <button onClick={onClose} style={styles.closeBtn}>&times;</button>
        </div>

        {loading && <div style={styles.center}>Loading analysis...</div>}
        {error && <div style={{ ...styles.center, color: '#d63031' }}>{error}</div>}

        {data && (
          <div style={styles.body}>
            {/* Summary */}
            <div style={styles.summaryRow}>
              <div style={styles.summaryCard}>
                <div style={styles.summaryValue}>{data.total_errors?.toLocaleString()}</div>
                <div style={styles.summaryLabel}>Total Errors</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={{ ...styles.summaryValue, color: '#3498db' }}>{data.israel_robots?.toLocaleString()}</div>
                <div style={styles.summaryLabel}>Israel Robots</div>
              </div>
              <div style={styles.summaryCard}>
                <div style={{ ...styles.summaryValue, color: '#e67e22' }}>{data.india_robots?.toLocaleString()}</div>
                <div style={styles.summaryLabel}>India Robots</div>
              </div>
            </div>

            {/* Subcategories (Drive Motor / Rotor Cleaning) */}
            {isDriveOrRotor && data.subcategories && (
              <Section title="Subcategories">
                <div style={styles.subGrid}>
                  {data.subcategories.map(s => (
                    <div key={s.name} style={styles.subCard}>
                      <div style={{ fontWeight: 700, fontSize: 18, color: '#2d3436' }}>{s.total.toLocaleString()}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#636e72' }}>{s.name}</div>
                      <div style={{ fontSize: 11, color: '#636e72', marginTop: 4 }}>
                        IL: {s.israel.toLocaleString()} · IN: {s.india.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Error Code Breakdown Table */}
            <Section title="Error Codes — Israel vs India">
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Error Code</th>
                    <th style={styles.thR}>Total</th>
                    <th style={{ ...styles.thR, color: '#3498db' }}>IL Count</th>
                    <th style={{ ...styles.thR, color: '#3498db' }}>IL/Robot</th>
                    {isComm && <th style={{ ...styles.thR, color: '#3498db' }}>IL Robots</th>}
                    <th style={{ ...styles.thR, color: '#e67e22' }}>IN Count</th>
                    <th style={{ ...styles.thR, color: '#e67e22' }}>IN/Robot</th>
                    {isComm && <th style={{ ...styles.thR, color: '#e67e22' }}>IN Robots</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.error_breakdown?.map(e => (
                    <tr key={e.code} style={styles.tr}>
                      <td style={styles.td}><code style={styles.code}>{e.code}</code></td>
                      <td style={styles.tdR}>{e.total.toLocaleString()}</td>
                      <td style={styles.tdR}>{e.israel_count.toLocaleString()}</td>
                      <td style={styles.tdR}><b>{e.israel_per_robot}</b></td>
                      {isComm && <td style={styles.tdR}>{e.israel_unique_robots.toLocaleString()}</td>}
                      <td style={styles.tdR}>{e.india_count.toLocaleString()}</td>
                      <td style={styles.tdR}><b>{e.india_per_robot}</b></td>
                      {isComm && <td style={styles.tdR}>{e.india_unique_robots.toLocaleString()}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>

            {/* Location Repeat Analysis (Drive / Rotor) */}
            {isDriveOrRotor && (
              <Section title="Repeating Location Analysis (same robot, same spot +/-10m)">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {['israel', 'india'].map(c => {
                    const lr = data[`${c}_location_repeat`];
                    if (!lr) return null;
                    return (
                      <div key={c} style={styles.locCard}>
                        <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>
                          {c === 'israel' ? '🇮🇱 Israel' : '🇮🇳 India'}
                        </h4>
                        <InfoRow label="Errors with location" value={lr.total_with_location?.toLocaleString()} />
                        <InfoRow label="Repeat groups" value={lr.repeat_groups?.toLocaleString()} />
                        <InfoRow label="Errors at repeat spots" value={lr.repeat_errors?.toLocaleString()} />
                        <InfoRow label="% repeating" value={`${lr.pct_repeat}%`} highlight={lr.pct_repeat > 80} />
                        <InfoRow label="Avg errors/point" value={lr.avg_per_point} />
                        <InfoRow label="Max at one point" value={lr.max_at_point?.toLocaleString()} />
                      </div>
                    );
                  })}
                </div>

                {/* Top 10 repeat points */}
                {data.top10_repeat_points?.length > 0 && (
                  <>
                    <h4 style={{ fontSize: 13, marginTop: 16 }}>Top 10 Repeat Points (robot + location)</h4>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>#</th>
                          <th style={styles.th}>Robot</th>
                          <th style={styles.th}>Site</th>
                          <th style={styles.th}>Country</th>
                          <th style={styles.thR}>Row</th>
                          <th style={styles.thR}>Zone (m)</th>
                          <th style={styles.thR}>Errors</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top10_repeat_points.map((r, i) => (
                          <tr key={i} style={styles.tr}>
                            <td style={styles.td}>{i + 1}</td>
                            <td style={styles.td}><b>{r.robot}</b></td>
                            <td style={styles.td}>{r.site}</td>
                            <td style={styles.td}>{r.country === 'Israel' ? '🇮🇱' : '🇮🇳'}</td>
                            <td style={styles.tdR}>{r.row}</td>
                            <td style={styles.tdR}>{r.zone_m}</td>
                            <td style={{ ...styles.tdR, fontWeight: 700, color: '#d63031' }}>{r.count}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}

                {/* Top 10 sites */}
                {data.top10_repeat_sites?.length > 0 && (
                  <>
                    <h4 style={{ fontSize: 13, marginTop: 16 }}>Top 10 Sites — Most Same-Location Errors</h4>
                    <table style={styles.table}>
                      <thead>
                        <tr>
                          <th style={styles.th}>#</th>
                          <th style={styles.th}>Site</th>
                          <th style={styles.th}>Country</th>
                          <th style={styles.thR}>Repeat Errors</th>
                          <th style={styles.thR}>Repeat Points</th>
                          <th style={styles.thR}>Total Robots</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.top10_repeat_sites.map((r, i) => (
                          <tr key={i} style={styles.tr}>
                            <td style={styles.td}>{i + 1}</td>
                            <td style={styles.td}><b>{r.site}</b></td>
                            <td style={styles.td}>{r.country === 'Israel' ? '🇮🇱' : '🇮🇳'}</td>
                            <td style={{ ...styles.tdR, fontWeight: 700 }}>{r.repeat_errors?.toLocaleString()}</td>
                            <td style={styles.tdR}>{r.repeat_points?.toLocaleString()}</td>
                            <td style={styles.tdR}>{r.total_robots}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </Section>
            )}

            {/* Communication: Dead-zone correlation */}
            {isComm && data.comm_correlation && (
              <Section title="Dead Zone Analysis — NO_EP_REPLY vs NO_REPLY_FOR_CLEAN_CMD">
                <div style={styles.corrGrid}>
                  <div style={styles.corrCard}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#d63031' }}>
                      {data.comm_correlation.robots_with_both?.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 12, color: '#636e72' }}>Robots with BOTH errors</div>
                    <div style={{ fontSize: 11, color: '#b2bec3', marginTop: 4 }}>Likely in weak-signal zones</div>
                  </div>
                  <div style={styles.corrCard}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#e67e22' }}>
                      {data.comm_correlation.robots_only_no_ep?.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 12, color: '#636e72' }}>Only NO_EP_REPLY</div>
                  </div>
                  <div style={styles.corrCard}>
                    <div style={{ fontSize: 24, fontWeight: 700, color: '#3498db' }}>
                      {data.comm_correlation.robots_only_no_clean?.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 12, color: '#636e72' }}>Only NO_REPLY_FOR_CLEAN</div>
                  </div>
                </div>
              </Section>
            )}

            {/* Battery: Voltage analysis */}
            {isBattery && data.reset_voltage_analysis && (
              <Section title="Reset Root Cause — Battery vs Other">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div style={styles.locCard}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Voltage Analysis</h4>
                    <InfoRow label="Total reset errors" value={data.reset_voltage_analysis.total_reset_errors?.toLocaleString()} />
                    <InfoRow label="With voltage data" value={data.reset_voltage_analysis.with_voltage_data?.toLocaleString()} />
                    <InfoRow label="Without voltage data" value={data.reset_voltage_analysis.without_voltage_data?.toLocaleString()} />
                    <div style={{ borderTop: '1px solid #eee', margin: '8px 0' }} />
                    <InfoRow label="🔋 Low battery cause" value={data.reset_voltage_analysis.low_battery_cause?.toLocaleString()} highlight />
                    <InfoRow label="⚡ Other cause" value={data.reset_voltage_analysis.other_cause?.toLocaleString()} />
                    <InfoRow label="% battery-caused" value={`${data.reset_voltage_analysis.pct_battery_cause}%`} highlight />
                    <div style={{ fontSize: 11, color: '#b2bec3', marginTop: 6 }}>
                      Threshold: {data.reset_voltage_analysis.threshold}
                    </div>
                  </div>

                  {data.reset_correlation && (
                    <div style={styles.locCard}>
                      <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>Reset Correlation</h4>
                      <InfoRow label="Robots with BOTH reset types" value={data.reset_correlation.robots_with_both?.toLocaleString()} highlight />
                      <InfoRow label="Errors from both-type robots" value={data.reset_correlation.errors_from_both?.toLocaleString()} />
                      <InfoRow label="Robots with single type only" value={data.reset_correlation.robots_single_type?.toLocaleString()} />
                      <InfoRow label="Errors from single-type" value={data.reset_correlation.errors_single_type?.toLocaleString()} />
                      <div style={{ fontSize: 11, color: '#b2bec3', marginTop: 6 }}>
                        {data.reset_correlation.interpretation}
                      </div>
                    </div>
                  )}
                </div>
              </Section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 10px', color: '#2d3436', borderBottom: '2px solid #6c5ce7', paddingBottom: 6 }}>{title}</h3>
      {children}
    </div>
  );
}

function InfoRow({ label, value, highlight }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 13 }}>
      <span style={{ color: '#636e72' }}>{label}</span>
      <span style={{ fontWeight: 600, color: highlight ? '#d63031' : '#2d3436' }}>{value}</span>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.5)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 20,
  },
  modal: {
    background: '#fff', borderRadius: 16, width: '90vw', maxWidth: 1100,
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 24px', borderBottom: '1px solid #eee', flexShrink: 0,
  },
  closeBtn: {
    background: 'none', border: 'none', fontSize: 28, cursor: 'pointer',
    color: '#636e72', padding: '0 8px', lineHeight: 1,
  },
  body: {
    padding: 24, overflowY: 'auto', flex: 1,
  },
  center: {
    padding: 60, textAlign: 'center', fontSize: 15, color: '#636e72',
  },
  summaryRow: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20,
  },
  summaryCard: {
    background: '#f8f9fa', borderRadius: 10, padding: '14px 16px', textAlign: 'center',
  },
  summaryValue: { fontSize: 24, fontWeight: 700, color: '#2d3436' },
  summaryLabel: { fontSize: 12, color: '#636e72', marginTop: 2 },
  subGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10,
  },
  subCard: {
    background: '#f8f9fa', borderRadius: 8, padding: 12, textAlign: 'center',
  },
  table: {
    width: '100%', borderCollapse: 'collapse', fontSize: 12,
  },
  th: {
    textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #eee',
    fontWeight: 600, color: '#636e72', fontSize: 11, whiteSpace: 'nowrap',
  },
  thR: {
    textAlign: 'right', padding: '8px 10px', borderBottom: '2px solid #eee',
    fontWeight: 600, color: '#636e72', fontSize: 11, whiteSpace: 'nowrap',
  },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '6px 10px', fontSize: 12 },
  tdR: { padding: '6px 10px', fontSize: 12, textAlign: 'right' },
  code: {
    background: '#f0f0f0', padding: '2px 6px', borderRadius: 4,
    fontSize: 11, fontFamily: 'monospace', whiteSpace: 'nowrap',
  },
  locCard: {
    background: '#f8f9fa', borderRadius: 10, padding: 16,
  },
  corrGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12,
  },
  corrCard: {
    background: '#f8f9fa', borderRadius: 10, padding: 16, textAlign: 'center',
  },
};
